import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, IconButton, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getAllConfigs } from '../../services/configService';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { COLORS, FONTS } from '../../theme/tokens';

const HomepagePopup = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [popupConfig, setPopupConfig] = useState(null);

  useEffect(() => {
    const fetchPopupConfig = async () => {
      try {
        const response = await getAllConfigs();
        if (response.success && response.data && response.data.popup) {
          const config = response.data.popup;

          // Check if popup is enabled
          if (!config.enabled) {
            return;
          }

          // Check if popup should be shown based on date and time
          if (shouldShowPopup(config)) {
            // Create a unique key based on date range to track if user has seen this specific popup campaign
            const popupKey = `popup_seen_${config.startDate}_${config.endDate}`;
            const hasSeenPopup = localStorage.getItem(popupKey);

            if (!hasSeenPopup) {
              setPopupConfig(config);
              setOpen(true);
              localStorage.setItem(popupKey, 'true');
            }
          }
        }
      } catch (error) {
        console.error('Error fetching popup config:', error);
      }
    };

    fetchPopupConfig();
  }, []);

  const shouldShowPopup = (config) => {
    // Get current time in Riga timezone (Europe/Riga)
    const rigaTime = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Riga' }));

    // Check date range
    if (config.startDate) {
      const startDate = new Date(config.startDate + 'T00:00:00');
      if (rigaTime < startDate) {
        return false;
      }
    }

    if (config.endDate) {
      const endDate = new Date(config.endDate + 'T23:59:59');
      if (rigaTime > endDate) {
        return false;
      }
    }

    // Check time range
    if (config.startTime && config.endTime) {
      const currentTime = rigaTime.getHours() * 60 + rigaTime.getMinutes();

      const [startHour, startMin] = config.startTime.split(':').map(Number);
      const startTimeMinutes = startHour * 60 + startMin;

      const [endHour, endMin] = config.endTime.split(':').map(Number);
      const endTimeMinutes = endHour * 60 + endMin;

      if (currentTime < startTimeMinutes || currentTime > endTimeMinutes) {
        return false;
      }
    }

    return true;
  };

  const handleClose = () => {
    setOpen(false);
  };

  if (!popupConfig || !open) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '2px',
          position: 'relative',
          maxWidth: 720,
          boxShadow: '0 20px 60px rgba(35,48,42,0.22)',
          border: `1px solid ${COLORS.borderHairline}`
        }
      }}
    >
      <IconButton
        aria-label={t('common.closePopup')}
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: COLORS.green,
          bgcolor: '#fff',
          border: `1px solid ${COLORS.borderHairline}`,
          zIndex: 1
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ p: { xs: 3, md: 5 }, pt: { xs: 6, md: 6 }, bgcolor: COLORS.surfacePanel }}>
        <Box
          sx={{
            '& img': {
              maxWidth: '100%',
              height: 'auto'
            },
            '& a': {
              color: COLORS.green,
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline'
              }
            },
            '& h1, & h2, & h3': { fontFamily: FONTS.heading, color: COLORS.textPrimary, lineHeight: 1.2 },
            '& p': { color: COLORS.textBody, lineHeight: 1.7 },
            '& button': { borderRadius: '2px' }
          }}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(popupConfig.content) }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default HomepagePopup;
